import React, { useState } from "react";
import { motion } from "framer-motion";
import { getFontAwesomeIcon } from "../../../../../../utils";

// HOOKS
import { useApprovalsModalViewModel } from "../../viewModels/useApprovalsModalViewModel";
import { useApprovalDecisionsViewModel } from "../../viewModels/useApprovalDecisionsViewModel";

// COMPONENTS
import { BaseToolTip, LoadingMessage } from "../../../../../../components";
import OrderDetailsContentSection from "../OrderDetailsContentSection";
import SingleApprovalDecisionForm from "./SingleApprovalDecisionForm";
import ApprovalFlowForm from "./ApprovalFlowForm";
// TYPES

const modalVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
	exit: { opacity: 0 },
};

export interface ApprovalDecisionsModalProps {
	isOpen: boolean;
	onClose: () => void;
	modalSelected: string;
	setRefetchData?: (value: boolean) => void;
	refetchData?: boolean;
	updateRowData?: (uuid: string, key: string, newValue: string) => void;
	setDeactivatedQuickMenuIds?: React.Dispatch<React.SetStateAction<string[]>>;
	handleBigModalClose?: () => void;
	onUpdate?: () => void;
	isApproveButtonEnabled?: boolean;
	currencyCharacter?: string;
	parentModalClose?: () => void;
}

const ApprovalDecisionsModal = ({
	onClose,
	isOpen,
	modalSelected,
	updateRowData,
	isApproveButtonEnabled,
	currencyCharacter,
	parentModalClose,
}: ApprovalDecisionsModalProps) => {
	//// HOOKS ////
	const {
		orderDetails,
		fields,
		showLoadingState,
		approvalDecisions,
		activeUuid,
	} = useApprovalsModalViewModel();

	const { isLoadingCreateDecision } = useApprovalDecisionsViewModel({
		approvalDecisions,
		decisionType: modalSelected,
		fields,
		activeUuid: activeUuid as string,
	});

	const [isGuardrailModalOpen, setIsGuardrailModalOpen] =
		useState<boolean>(false);

	let modalFields = fields?.approvalActionModal?.[modalSelected];

	////////////////////////////////////////////////////////////////// CLASSNAMES //////////////////////////////////////////////////////////////////

	const renderCorrectGridStyles = (): string => {
		const baseClasses = "w-full sm:grid flex-col gap-x-2 gap-y-2";
		const gridCols = "grid-cols-3";
		return `${baseClasses} ${gridCols}`;
	};

	////////////////////////////////////////////////////////////////// DATA TO VARS ///////////////////////////////////////////////////////////////

	const handleBackgroundClick = () => {
		if (isGuardrailModalOpen) {
			onClose();
			setIsGuardrailModalOpen(false);
			return;
		}

		setIsGuardrailModalOpen(true);
	};
	const handleModalClick = (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		e.stopPropagation();
	};

	const orderDetailsSection = (
		<>
			<OrderDetailsContentSection
				data={orderDetails}
				sectionContainerClasses="w-full max-xl:w-full"
				detailsContainerClasses={renderCorrectGridStyles()}
				fields={fields?.approvalActionModal?.orderContentDetails}
				itemContainerClasses="flex"
				mobileBreakpoint="flex"
				approvalDecisions={approvalDecisions}
				currencyCharacter={currencyCharacter}
			/>
			<div className="border-b border-navy-200 w-full"></div>
		</>
	);

	const orderDetailsSectionApprovalWorkflow = (
		<>
			<OrderDetailsContentSection
				data={orderDetails}
				sectionContainerClasses="w-full max-xl:w-full px-5 pt-5"
				detailsContainerClasses={
					"w-full sm:grid flex-col gap-x-2 gap-y-4 grid-cols-7"
				}
				fields={
					fields?.approvalActionModal?.orderContentDetailsWorkflow
				}
				itemContainerClasses="flex-col"
				mobileBreakpoint="flex"
				defaultFlexDirection="flex-col"
				approvalDecisions={approvalDecisions}
				currencyCharacter={currencyCharacter}
			/>
			<div className="border-b border-navy-200 w-full pb-5"></div>
		</>
	);

	return (
		<>
			{isOpen && (
				<motion.div
					className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-50"
					variants={modalVariants}
					initial="hidden"
					animate="visible"
					onClick={handleBackgroundClick}
				>
					<div
						className={`bg-white relative rounded-2xl  top-5  ${
							modalSelected === "adminOnly"
								? "w-[1024px] h-[800px] max-sm:min-w-[400px]"
								: "w-[600px] max-sm:min-w-[400px]"
						}`}
						onClick={handleModalClick}
					>
						{showLoadingState ||
						isLoadingCreateDecision ||
						!fields ? (
							<div className="flex items-center justify-center w-full h-[540px] min-h-full ">
								<LoadingMessage message="" />
							</div>
						) : (
							<div className="flex flex-col justify-center w-full h-full p-5 gap-5">
								<div className="flex">
									<div
										className={`text-${modalFields?.title?.prefixColor} text-xl mr-2`}
									>
										{getFontAwesomeIcon(
											modalFields?.title?.prefix,
											modalFields?.title?.prefixWeight,
										)}
									</div>
									<div className="text-navy-500 font-omnes-medium text-xl flex w-full mr-5">
										{modalFields?.title?.label}
										{modalFields?.title?.hasToolTip && (
											<div className="flex items-center">
												<BaseToolTip
													message={
														modalFields?.title
															?.toolTipText
													}
													icon={
														modalFields?.title
															?.hasToolTip
													}
													toolTipPlacement="right"
												/>
											</div>
										)}
									</div>
									<button
										className="text-navy-300 hover:cursor-pointer hover:text-gray-800 text-xl"
										onClick={onClose}
										aria-label={"Close modal"}
									>
										{getFontAwesomeIcon("xmark", "regular")}
									</button>
								</div>
								<div className="flex flex-col gap-2 flex-grow overflow-hidden">
									{modalSelected === "adminOnly" ? (
										<div className="flex flex-col h-full ">
											{
												orderDetailsSectionApprovalWorkflow
											}
											<ApprovalFlowForm
												isApproveButtonEnabled={
													isApproveButtonEnabled
												}
												onClose={onClose}
												fields={modalFields}
												modalSelected={modalSelected}
												isOpen={isOpen}
												activeUuid={activeUuid ?? ""}
												updateRowData={updateRowData}
											/>
										</div>
									) : (
										<div className="flex flex-col gap-2 ">
											{orderDetailsSection}
											<SingleApprovalDecisionForm
												modalSelected={modalSelected}
												isApproveButtonEnabled={
													isApproveButtonEnabled
												}
												onClose={onClose}
												isOpen={isOpen}
												activeUuid={activeUuid ?? ""}
												fields={modalFields}
												updateRowData={updateRowData}
												parentModalClose={
													parentModalClose
												}
											/>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</motion.div>
			)}
		</>
	);
};

export default ApprovalDecisionsModal;
